import React, { useState, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sparkles, ScanBarcode, Grid2X2, BrainCircuit, Loader2, AlertTriangle } from 'lucide-react';
import { useAppStore, useAIToolsState } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { assignElementsToRoomsByFloorPlan } from '@/services/roomAssignmentService';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface AIToolsMenuProps {
  className?: string;
}

export function AIToolsMenu({ className = '' }: AIToolsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('fixtures');
  const [showReprocessFixturesDialog, setShowReprocessFixturesDialog] = useState(false);
  const [showReprocessRoomsDialog, setShowReprocessRoomsDialog] = useState(false);

  const { getToken } = useAuth();
  const aiTools = useAIToolsState();
  const {
    setConfidenceThreshold,
    setMarkerOpacity,
    setIsProcessing,
    setRoomConfidenceThreshold,
    setRoomOpacity,
    setIsProcessingRooms,
  } = useAppStore();

  const { data, floorPlan } = useFloorPlanContext();
  const floorPlanId = floorPlan.id;

  // Check if floor plan has AI-detected fixtures (not user-verified)
  const hasAIDetectedFixtures =
    Array.isArray(data.fixtures) &&
    data.fixtures.some(fixture => !fixture.user_verified && fixture.confidence_score !== null);

  // Check if floor plan has AI-detected rooms
  const hasAIDetectedRooms = Array.isArray(data.rooms) && data.rooms.length > 0;

  // Process floor plan with AI for fixture detection
  const handleProcessWithAI = useCallback(async () => {
    if (!floorPlanId || !data.imageUrl) {
      toast.error('Floor plan not ready for processing');
      return;
    }

    try {
      setIsProcessing(true);
      toast.info('Processing floor plan with AI. This may take a minute...', {
        duration: 5000,
        id: 'ai-processing',
      });

      // Try calling the edge function directly with fetch to bypass Supabase client auth issues
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const headers: any = {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
      };

      // Get Clerk token using React hook for reliability.
      // Use the 'supabase' JWT template so the token carries the org_id claim —
      // the edge functions' checkAuth/validateClerkToken reads it (authResult.orgId)
      // to authorize the floor-plan org-ownership check. The default getToken()
      // has no org_id, which previously made that check reject every request.
      const clerkToken = await getToken({ template: 'supabase' });
      if (!clerkToken) {
        logger.warn('No Clerk token available, edge function may fail in production');
      }
      logger.log('Clerk token obtained:', !!clerkToken, 'Length:', clerkToken?.length);

      if (clerkToken) {
        headers['Authorization'] = `Bearer ${clerkToken}`;
      }

      logger.log(
        'Calling edge function with direct fetch, auth header present:',
        !!headers.Authorization,
      );

      const response = await fetch(`${SUPABASE_URL}/functions/v1/process-floor-plan`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          floorPlanId,
          imageUrl: data.imageUrl,
          conf: aiTools.confidenceThreshold,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Edge function error response:', response.status, errorText);
        throw new Error(`Edge function error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      logger.log('Edge function response:', result);

      // Process the result
      if (result.error) {
        logger.error('Edge function error:', result.error);
        throw new Error(result.error);
      }

      logger.log('AI processing result:', result);

      toast.success('Floor plan processed successfully');

      // Refetch data to show new fixtures
      await data.refetch();

      // Auto-assign newly detected fixtures to existing rooms (if any)
      if (result?.success && result.fixtureCount > 0) {
        logger.log('Checking for existing rooms to assign fixtures...');
        try {
          // Check if floor plan has rooms
          const roomsExist = data.rooms && data.rooms.length > 0;

          if (roomsExist) {
            logger.log('Found existing rooms, assigning new fixtures...');
            const assignmentResult = await assignElementsToRoomsByFloorPlan(
              floorPlanId,
              aiTools.roomConfidenceThreshold,
              aiTools.confidenceThreshold, // Only assign visible fixtures
            );

            if (assignmentResult.total > 0) {
              logger.log(
                `Fixture assignment complete: ${assignmentResult.fixtures} fixtures assigned to rooms`,
              );
              toast.success(`Assigned ${assignmentResult.fixtures} fixtures to rooms`, {
                duration: 5000,
              });

              // Refetch again to show updated room assignments
              await data.refetch();
            } else {
              logger.log(
                'No fixtures needed room assignment (all outside rooms or already assigned)',
              );
            }

            if (assignmentResult.errors.length > 0) {
              logger.warn(`${assignmentResult.errors.length} errors occurred during assignment`);
            }
          } else {
            logger.log('No rooms detected yet - skipping auto-assignment');
          }
        } catch (assignmentError) {
          logger.error('Error assigning fixtures to rooms:', assignmentError);
          // Non-fatal error - fixture detection still succeeded
          toast.warning(
            'Fixtures detected successfully, but some could not be assigned to rooms automatically',
          );
        }
      }
    } catch (error) {
      logger.error('Error processing floor plan with AI:', error);
      toast.error('Failed to process floor plan with AI');
    } finally {
      setIsProcessing(false);
    }
  }, [floorPlanId, data, setIsProcessing, aiTools.confidenceThreshold]);

  // Store mask quality metrics state
  const [maskQualityMetrics, setMaskQualityMetrics] = React.useState<{
    high: number;
    medium: number;
    fallback: number;
    averagePolygonPoints: number;
  } | null>(null);

  // Process floor plan with AI for room detection
  const handleDetectRooms = useCallback(async () => {
    if (!floorPlanId || !data.imageUrl) {
      toast.error('Floor plan not ready for room detection');
      return;
    }

    try {
      logger.log('Starting room detection for floor plan:', floorPlanId);
      logger.log('Current room count:', data.rooms?.length || 0);

      setIsProcessingRooms(true);
      setMaskQualityMetrics(null); // Reset metrics
      toast.info('Detecting rooms with AI. This may take a minute...', {
        duration: 5000,
        id: 'ai-room-processing',
      });

      // Call the Supabase edge function for room detection using Gemini AI
      logger.log('Invoking detect-rooms edge function with:', {
        floorPlanId,
        imageUrl: data.imageUrl?.substring(0, 100) + '...',
      });

      // Get Clerk token using React hook for reliability.
      // Use the 'supabase' JWT template so the token carries the org_id claim —
      // the edge functions' checkAuth/validateClerkToken reads it (authResult.orgId)
      // to authorize the floor-plan org-ownership check. The default getToken()
      // has no org_id, which previously made that check reject every request.
      const clerkToken = await getToken({ template: 'supabase' });
      if (!clerkToken) {
        logger.warn('No Clerk token available, room detection may fail in production');
      }
      logger.log(
        'Room detection - Clerk token obtained:',
        !!clerkToken,
        'Length:',
        clerkToken?.length,
      );

      // Try calling the edge function directly with fetch to bypass Supabase client auth issues
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const headers: any = {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
      };

      // Add Clerk token if available
      if (clerkToken) {
        headers['Authorization'] = `Bearer ${clerkToken}`;
      }

      logger.log(
        'Calling detect-rooms with direct fetch, auth header present:',
        !!headers.Authorization,
      );

      const response = await fetch(`${SUPABASE_URL}/functions/v1/detect-rooms`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          floorPlanId,
          imageUrl: data.imageUrl,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Room detection error response:', response.status, errorText);
        throw new Error(`Room detection error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      if (result.error) {
        logger.error('Room detection error:', result.error);
        throw new Error(result.error);
      }

      logger.log('Room detection result:', result);

      // Check if rooms were actually inserted
      if (result?.success) {
        logger.log(`Successfully detected ${result.roomCount} rooms`);
        if (result.totalDetected !== result.roomCount) {
          logger.warn(
            `Warning: Detected ${result.totalDetected} rooms but only inserted ${result.roomCount}`,
          );
        }
        if (result.errors && result.errors.length > 0) {
          logger.error('Some rooms failed to insert:', result.errors);
        }

        // Store mask quality metrics for display
        if (result.maskQuality) {
          logger.log('Mask quality metrics:', result.maskQuality);
          setMaskQualityMetrics(result.maskQuality);
        }

        // Build quality summary for toast
        const qualitySummary = result.maskQuality
          ? ` (${result.maskQuality.high} high-quality, ${result.maskQuality.medium} medium, ${result.maskQuality.fallback} fallback)`
          : '';

        toast.success(
          `Room detection completed successfully. Detected ${result.roomCount} rooms${qualitySummary}.`,
        );

        // Automatically enable room visibility after successful detection
        const setRoomVisibility = useAppStore.getState().setRoomVisibility;
        setRoomVisibility(true);
      } else {
        logger.error('Room detection failed:', result?.error || 'Unknown error');
        toast.error(`Room detection failed: ${result?.error || 'Unknown error'}`);
      }

      // Refetch data to show new rooms
      await data.refetch();

      // Assign existing elements to newly detected rooms
      if (result?.success && result.roomCount > 0) {
        logger.log('Assigning existing elements to detected rooms...');
        try {
          // Fetch fresh data from database to ensure we have the latest rooms
          const assignmentResult = await assignElementsToRoomsByFloorPlan(
            floorPlanId,
            aiTools.roomConfidenceThreshold,
            aiTools.confidenceThreshold, // Only assign visible fixtures
          );

          if (assignmentResult.total > 0) {
            logger.log(
              `Element assignment complete: ${assignmentResult.total} elements assigned (${assignmentResult.fixtures} fixtures, ${assignmentResult.controls} controls, ${assignmentResult.lvProducts} products)`,
            );
            toast.success(
              `Assigned ${assignmentResult.total} elements to rooms (${assignmentResult.fixtures} fixtures, ${assignmentResult.controls} controls, ${assignmentResult.lvProducts} products)`,
              { duration: 5000 },
            );

            // Refetch again to show updated room assignments
            await data.refetch();
          } else {
            logger.log('No elements needed assignment (all already assigned or outside rooms)');
          }

          if (assignmentResult.errors.length > 0) {
            logger.warn(
              `${assignmentResult.errors.length} errors occurred during element assignment`,
            );
          }
        } catch (assignmentError) {
          logger.error('Error assigning elements to rooms:', assignmentError);
          // Non-fatal error - room detection still succeeded
          toast.warning(
            'Rooms detected successfully, but some elements could not be assigned automatically',
          );
        }
      }
    } catch (error) {
      logger.error('Error detecting rooms:', error);
      toast.error('Failed to detect rooms');
    } finally {
      setIsProcessingRooms(false);
    }
  }, [floorPlanId, data, setIsProcessingRooms]);

  // Filter fixtures based on confidence threshold
  const filteredFixtures = Array.isArray(data.fixtures)
    ? data.fixtures.filter(
        fixture => (fixture.confidence_score || 0) >= aiTools.confidenceThreshold,
      )
    : [];

  // Filter rooms based on confidence threshold
  const filteredRooms = Array.isArray(data.rooms)
    ? data.rooms.filter(room => (room.confidence || 0) >= aiTools.roomConfidenceThreshold)
    : Array.isArray(data.rooms?.data)
      ? data.rooms.data.filter(room => (room.confidence || 0) >= aiTools.roomConfidenceThreshold)
      : [];

  return (
    <TooltipProvider delayDuration={500}>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className={`h-8 w-8 p-0 ${className}`}>
                <Sparkles className="w-4 h-4" style={{ color: '#8B5CF6' }} />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>AI Analysis Tools</p>
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-[320px]">
          <div className="p-4 w-full">
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">AI Tools</h3>
                <p className="text-xs text-muted-foreground">
                  Use AI to automatically detect elements in your floor plan.
                </p>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="fixtures" className="text-xs">
                    <ScanBarcode className="w-3 h-3 mr-1" /> Lighting
                  </TabsTrigger>
                  <TabsTrigger value="rooms" className="text-xs">
                    <Grid2X2 className="w-3 h-3 mr-1" /> Rooms
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="fixtures" className="mt-4">
                  <FixtureDetectionContent
                    onProcessWithAI={handleProcessWithAI}
                    isProcessing={aiTools.isProcessing}
                    isOtherProcessing={aiTools.isProcessingRooms}
                    confidenceThreshold={aiTools.confidenceThreshold}
                    setConfidenceThreshold={setConfidenceThreshold}
                    markerOpacity={aiTools.markerOpacity}
                    setMarkerOpacity={setMarkerOpacity}
                    fixtures={filteredFixtures}
                    allFixtures={Array.isArray(data.fixtures) ? data.fixtures : []}
                    isProcessed={hasAIDetectedFixtures}
                    showReprocessDialog={showReprocessFixturesDialog}
                    setShowReprocessDialog={setShowReprocessFixturesDialog}
                  />
                </TabsContent>

                <TabsContent value="rooms" className="mt-4">
                  <RoomDetectionContent
                    onDetectRooms={handleDetectRooms}
                    isProcessing={aiTools.isProcessingRooms}
                    isOtherProcessing={aiTools.isProcessing}
                    confidenceThreshold={aiTools.roomConfidenceThreshold}
                    setConfidenceThreshold={setRoomConfidenceThreshold}
                    roomOpacity={aiTools.roomOpacity}
                    setRoomOpacity={setRoomOpacity}
                    rooms={filteredRooms}
                    allRooms={
                      Array.isArray(data.rooms)
                        ? data.rooms
                        : Array.isArray(data.rooms?.data)
                          ? data.rooms.data
                          : []
                    }
                    isProcessed={hasAIDetectedRooms}
                    showReprocessDialog={showReprocessRoomsDialog}
                    setShowReprocessDialog={setShowReprocessRoomsDialog}
                    maskQualityMetrics={maskQualityMetrics}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}

// Fixture Detection Content Component
interface FixtureDetectionContentProps {
  onProcessWithAI: () => Promise<void>;
  isProcessing: boolean;
  isOtherProcessing: boolean;
  confidenceThreshold: number;
  setConfidenceThreshold: (value: number) => void;
  markerOpacity: number;
  setMarkerOpacity: (value: number) => void;
  fixtures: any[];
  allFixtures: any[];
  isProcessed: boolean;
  showReprocessDialog: boolean;
  setShowReprocessDialog: (show: boolean) => void;
}

function FixtureDetectionContent({
  onProcessWithAI,
  isProcessing,
  isOtherProcessing,
  confidenceThreshold,
  setConfidenceThreshold,
  markerOpacity,
  setMarkerOpacity,
  fixtures,
  allFixtures,
  isProcessed,
  showReprocessDialog,
  setShowReprocessDialog,
}: FixtureDetectionContentProps) {
  const handleProcessButtonClick = async () => {
    if (isProcessed) {
      // Show confirmation dialog for reprocessing
      setShowReprocessDialog(true);
    } else {
      // First time processing, no confirmation needed
      try {
        await onProcessWithAI();
      } catch (error) {
        logger.error('Error processing with AI:', error);
      }
    }
  };

  const handleConfirmReprocess = async () => {
    setShowReprocessDialog(false);
    try {
      await onProcessWithAI();
    } catch (error) {
      logger.error('Error reprocessing with AI:', error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h4 className="text-sm font-semibold">Detect Lighting Fixtures</h4>
        <p className="text-xs text-muted-foreground">
          Use AI to automatically detect lighting fixtures in your floor plan.
        </p>
      </div>

      {/* Confidence Threshold */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label htmlFor="confidence" className="text-xs">
            Confidence Threshold
          </Label>
          <span className="text-xs font-medium">{Math.round(confidenceThreshold * 100)}%</span>
        </div>
        <Slider
          id="confidence"
          min={0}
          max={100}
          step={1}
          value={[confidenceThreshold * 100]}
          onValueChange={values => setConfidenceThreshold(values[0] / 100)}
        />
        <p className="text-xs text-muted-foreground">
          Adjust to filter fixtures based on detection confidence.
        </p>
      </div>

      {/* Marker Opacity */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label htmlFor="opacity" className="text-xs">
            Marker Opacity
          </Label>
          <span className="text-xs font-medium">{Math.round(markerOpacity * 100)}%</span>
        </div>
        <Slider
          id="opacity"
          min={0}
          max={100}
          step={5}
          value={[markerOpacity * 100]}
          onValueChange={values => setMarkerOpacity(values[0] / 100)}
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>0%</span>
          <span>{Math.round(markerOpacity * 100)}%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Process Button */}
      <Button
        className="w-full"
        onClick={handleProcessButtonClick}
        disabled={isProcessing || isOtherProcessing}
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : isOtherProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Room Detection Running...
          </>
        ) : (
          <>
            <BrainCircuit className="mr-2 h-4 w-4" />
            {isProcessed ? 'Re-Process with AI' : 'Process with AI'}
          </>
        )}
      </Button>

      {/* Status Info */}
      {allFixtures.length > 0 && (
        <div className="text-xs text-muted-foreground">
          <p>Detected: {allFixtures.length} fixtures</p>
          <p>Showing: {fixtures.length} fixtures (based on confidence)</p>
        </div>
      )}

      {/* Reprocess Confirmation Dialog */}
      <AlertDialog open={showReprocessDialog} onOpenChange={setShowReprocessDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Reprocess Lighting Detection?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>This floor plan already has AI-detected lighting fixtures.</p>
                <p className="font-semibold">
                  Previously detected fixtures will be deleted and the floor plan will be
                  reprocessed.
                </p>
                <p className="text-sm text-muted-foreground">
                  Note: Manually added fixtures will be preserved.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmReprocess}
              className="bg-destructive hover:bg-destructive/90"
            >
              Reprocess
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Room Detection Content Component
interface RoomDetectionContentProps {
  onDetectRooms: () => Promise<void>;
  isProcessing: boolean;
  isOtherProcessing: boolean;
  confidenceThreshold: number;
  setConfidenceThreshold: (value: number) => void;
  roomOpacity: number;
  setRoomOpacity: (value: number) => void;
  rooms: any[];
  allRooms: any[];
  isProcessed: boolean;
  showReprocessDialog: boolean;
  setShowReprocessDialog: (show: boolean) => void;
  maskQualityMetrics: {
    high: number;
    medium: number;
    fallback: number;
    averagePolygonPoints: number;
  } | null;
}

function RoomDetectionContent({
  onDetectRooms,
  isProcessing,
  isOtherProcessing,
  confidenceThreshold,
  setConfidenceThreshold,
  roomOpacity,
  setRoomOpacity,
  rooms,
  allRooms,
  isProcessed,
  showReprocessDialog,
  setShowReprocessDialog,
  maskQualityMetrics,
}: RoomDetectionContentProps) {
  // Get wall visibility state and toggle action from store
  const wallsVisible = useAppStore(state => state.rooms.wallsVisible);
  const toggleWallVisibility = useAppStore(state => state.toggleWallVisibility);

  const handleDetectButtonClick = async () => {
    if (isProcessed) {
      // Show confirmation dialog for reprocessing
      setShowReprocessDialog(true);
    } else {
      // First time processing, no confirmation needed
      try {
        await onDetectRooms();
      } catch (error) {
        logger.error('Error detecting rooms with AI:', error);
      }
    }
  };

  const handleConfirmReprocess = async () => {
    setShowReprocessDialog(false);
    try {
      await onDetectRooms();
    } catch (error) {
      logger.error('Error redetecting rooms with AI:', error);
    }
  };

  return (
    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
      <div className="space-y-2">
        <h4 className="text-sm font-semibold">Detect Rooms</h4>
        <p className="text-xs text-muted-foreground">
          Use AI to automatically detect rooms in your floor plan.
        </p>
      </div>

      {/* Confidence Threshold */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label htmlFor="room-confidence" className="text-xs">
            Confidence Threshold
          </Label>
          <span className="text-xs font-medium">{Math.round(confidenceThreshold * 100)}%</span>
        </div>
        <Slider
          id="room-confidence"
          min={0}
          max={100}
          step={1}
          value={[confidenceThreshold * 100]}
          onValueChange={values => setConfidenceThreshold(values[0] / 100)}
        />
        <p className="text-xs text-muted-foreground">
          Adjust to filter rooms based on detection confidence.
        </p>
      </div>

      {/* Room Opacity */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label htmlFor="room-opacity" className="text-xs">
            Room Opacity
          </Label>
          <span className="text-xs font-medium">{Math.round(roomOpacity * 100)}%</span>
        </div>
        <Slider
          id="room-opacity"
          min={0}
          max={100}
          step={5}
          value={[roomOpacity * 100]}
          onValueChange={values => setRoomOpacity(values[0] / 100)}
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>0%</span>
          <span>{Math.round(roomOpacity * 100)}%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Wall Visibility Toggle - HIDDEN per user request */}
      {/* {allRooms.length > 0 && (
        <div className="flex items-center justify-between space-x-2 py-2 border-t border-border">
          <div className="space-y-0.5">
            <Label htmlFor="walls-visible" className="text-xs font-medium">
              Show Walls
            </Label>
            <p className="text-xs text-muted-foreground">
              Visualize detected walls (helps debug room detection)
            </p>
          </div>
          <Switch
            id="walls-visible"
            checked={wallsVisible}
            onCheckedChange={toggleWallVisibility}
          />
        </div>
      )} */}

      {/* Wall Statistics Dashboard */}
      {allRooms.length > 0 && wallsVisible && (
        <div className="rounded-md border border-border bg-muted/50 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold">Wall Detection</span>
            <span className="text-xs text-muted-foreground">Hybrid AI + CV</span>
          </div>

          {/* Wall Count vs Expected */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span>Total Walls Detected</span>
              <span className="font-medium">{allRooms.length > 0 ? '80-120 expected' : '-'}</span>
            </div>

            {/* Color Legend */}
            <div className="pt-2 space-y-1">
              <p className="text-xs font-medium mb-1">Wall Colors:</p>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span>High Confidence (&gt;0.5)</span>
                </div>
                <span className="text-muted-foreground">AI Detected</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <span>Medium (0.3-0.5)</span>
                </div>
                <span className="text-muted-foreground">Lower Confidence</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500" />
                  <span>Low (&lt;0.3)</span>
                </div>
                <span className="text-muted-foreground">Needs Review</span>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-border text-xs text-muted-foreground">
            <p>💡 More walls = Better room boundaries</p>
            <p className="mt-1">Expected ratio: 1.5-2 walls per room</p>
          </div>
        </div>
      )}

      {/* Detect Button */}
      <Button
        className="w-full"
        onClick={handleDetectButtonClick}
        disabled={isProcessing || isOtherProcessing}
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : isOtherProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Fixture Detection Running...
          </>
        ) : (
          <>
            <Grid2X2 className="mr-2 h-4 w-4" />
            {isProcessed ? 'Re-Detect Rooms' : 'Detect Rooms'}
          </>
        )}
      </Button>

      {/* Status Info */}
      {allRooms.length > 0 && (
        <div className="text-xs text-muted-foreground space-y-1">
          <p>Detected: {allRooms.length} rooms</p>
          <p>Showing: {rooms.length} rooms (based on confidence)</p>
        </div>
      )}

      {/* Mask Quality Metrics */}
      {maskQualityMetrics && (
        <div className="rounded-md border border-border bg-muted/50 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold">Detection Quality</span>
            <span className="text-xs text-muted-foreground">
              Avg: {maskQualityMetrics.averagePolygonPoints} points
            </span>
          </div>
          <div className="space-y-1">
            {maskQualityMetrics.high > 0 && (
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span>High Quality</span>
                </div>
                <span className="font-medium">{maskQualityMetrics.high}</span>
              </div>
            )}
            {maskQualityMetrics.medium > 0 && (
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-500" />
                  <span>Medium Quality</span>
                </div>
                <span className="font-medium">{maskQualityMetrics.medium}</span>
              </div>
            )}
            {maskQualityMetrics.fallback > 0 && (
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                  <span>Approximate</span>
                </div>
                <span className="font-medium">{maskQualityMetrics.fallback}</span>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground pt-1 border-t border-border">
            High-quality rooms use true boundary detection from AI masks
          </p>
        </div>
      )}

      {/* Reprocess Confirmation Dialog */}
      <AlertDialog open={showReprocessDialog} onOpenChange={setShowReprocessDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Redetect Rooms?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>This floor plan already has AI-detected rooms.</p>
                <p className="font-semibold">
                  Previously detected rooms will be deleted and the floor plan will be reprocessed.
                </p>
                <p className="text-sm text-muted-foreground">Note: This action cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmReprocess}
              className="bg-destructive hover:bg-destructive/90"
            >
              Redetect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
